import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';

const CartCtx = createContext(null);
const KEY = 'harmony_cart_v1';

export const useCart = () => {
  const c = useContext(CartCtx);
  if (!c) throw new Error('useCart must be inside CartProvider');
  return c;
};

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(KEY) || '[]');
    } catch (err) {
      console.warn('Cart: failed to load from localStorage', err);
      return [];
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(KEY, JSON.stringify(items));
    } catch (err) {
      console.warn('Cart: failed to persist to localStorage', err);
    }
  }, [items]);

  const add = useCallback((item) => {
    setItems((prev) => {
      const i = prev.findIndex((x) => x.sku === item.sku);
      if (i >= 0) {
        const next = [...prev];
        next[i] = { ...next[i], qty: next[i].qty + (item.qty || 1) };
        return next;
      }
      return [...prev, { ...item, qty: item.qty || 1 }];
    });
  }, []);

  const remove = useCallback((sku) => setItems((p) => p.filter((x) => x.sku !== sku)), []);
  const updateQty = useCallback((sku, qty) => {
    if (qty <= 0) return remove(sku);
    setItems((p) => p.map((x) => (x.sku === sku ? { ...x, qty } : x)));
  }, [remove]);
  const clear = useCallback(() => setItems([]), []);

  const subtotal = useMemo(
    () => items.reduce((sum, i) => sum + i.unit_price * i.qty, 0),
    [items],
  );
  const count = useMemo(
    () => items.reduce((sum, i) => sum + i.qty, 0),
    [items],
  );

  const value = useMemo(
    () => ({ items, add, remove, updateQty, clear, subtotal, count }),
    [items, add, remove, updateQty, clear, subtotal, count],
  );

  return <CartCtx.Provider value={value}>{children}</CartCtx.Provider>;
};
